import React, { useMemo, useState, useEffect } from 'react';
import { ref, onValue, update, remove } from 'firebase/database';
import { toast } from 'react-toastify';
import { FiArrowDown, FiArrowUp, FiCheckCircle, FiPackage, FiSearch, FiTrash2, FiRefreshCw } from 'react-icons/fi';

import { db } from '../../firebase';
import {
    TRACKED_APPS,
    PROTECTED_PACKAGES,
    getLatestAppVersion,
    needsAppUpdate,
} from '../../utils/appVersions';

// Firebase may return an array (with null holes) or an object map -> clean array.
function normalizeAppList(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === 'object') return Object.values(val).filter(Boolean);
    return [];
}

// Apps tab — lists every app installed on the device, with badges for
// the three apps we track (Player, Updater, Monitoring). Outdated tracked
// apps get a coloured "update available" pill so the operator knows at
// a glance whether an OTA push is overdue.

export default function AppsTab({ mac, device, apps }) {
    const list = device?.app_list || [];

    const [query, setQuery] = useState('');
    const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

    // Decorate each row with: tracked-or-not, latest version, needs-update.
    // Memoised because the lookup runs against the apps snapshot, which
    // may be large enough that a per-render scan is wasteful.
    const decorated = useMemo(() => {
        return list.map((app) => {
            const tracked = TRACKED_APPS.find((t) => t.packageName === app.packageName);
            const latest = tracked ? getLatestAppVersion(apps, app.packageName) : null;
            const installed = app.versionName || app.appVersion || null;
            return {
                packageName: app.packageName || '',
                appName: app.appName || app.packageName || 'Unknown',
                installed,
                tracked: Boolean(tracked),
                latest,
                outdated: tracked ? needsAppUpdate(installed, latest) : false,
            };
        });
    }, [list, apps]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const rows = q
            ? decorated.filter(
                  (r) =>
                      r.appName.toLowerCase().includes(q) ||
                      r.packageName.toLowerCase().includes(q)
              )
            : decorated;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            // Tracked apps always pin to the top — they're the operator's
            // primary concern. Among tracked, outdated bubble first.
            if (a.tracked !== b.tracked) return a.tracked ? -1 : 1;
            if (a.outdated !== b.outdated) return a.outdated ? -1 : 1;
            switch (sort.key) {
                case 'name':    return a.appName.localeCompare(b.appName) * dir;
                case 'package': return a.packageName.localeCompare(b.packageName) * dir;
                case 'version': return (a.installed || '').localeCompare(b.installed || '') * dir;
                default: return 0;
            }
        });
    }, [decorated, query, sort]);

    return (
        <div className="space-y-6">
            {list.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/[0.08] p-10 text-center text-gray-500">
                    <FiPackage className="mx-auto text-3xl mb-3 text-gray-500" />
                    No apps reported.
                </div>
            ) : (
                <section className="card-chrome overflow-hidden">
                    <header className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
                        <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
                            <FiPackage /> Installed Apps
                            <span className="text-xs text-gray-500 font-normal">({filtered.length} of {decorated.length})</span>
                        </h2>
                        <label className="relative">
                            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search apps…"
                                className="bg-surface-inset border border-white/[0.06] rounded-md pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand/40 min-w-[220px]"
                            />
                        </label>
                    </header>

                    <table className="w-full text-sm">
                        <thead className="bg-surface-inset text-xs uppercase tracking-wider text-gray-400">
                            <tr>
                                <SortableTh label="App" sortKey="name" sort={sort} setSort={setSort} />
                                <SortableTh label="Package" sortKey="package" sort={sort} setSort={setSort} />
                                <SortableTh label="Installed" sortKey="version" sort={sort} setSort={setSort} />
                                <th className="px-5 py-2.5 text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((row) => (
                                <AppRow key={row.packageName || row.appName} row={row} />
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            {/* Full device inventory (scan) - moved here from the License tab. */}
            <AllAppsSection mac={mac} />
        </div>
    );
}

// ---------------------------------------------------------------------
// Scan all apps — one-time FULL inventory of every app on the box (not just
// tracked). scan_all_apps -> monitoring writes mdm/devices/<MAC>/all_apps ->
// rendered here with per-app Uninstall + Clear. Commands are plain STRINGS
// (the monitoring reads command as String.class). Protected apps are blocked.
// ---------------------------------------------------------------------

function AllAppsSection({ mac }) {
    const [apps, setApps] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');

    useEffect(() => {
        if (!mac) return undefined;
        const r = ref(db, `mdm/devices/${mac}/all_apps`);
        const unsub = onValue(r, (snap) => {
            setApps(snap.val());
            setLoading(false);
        });
        return () => unsub();
    }, [mac]);

    const all = normalizeAppList(apps);
    const query = q.trim().toLowerCase();
    const shown = query
        ? all.filter(
              (a) =>
                  (a.appName || '').toLowerCase().includes(query) ||
                  (a.packageName || '').toLowerCase().includes(query)
          )
        : all;

    const send = async (command, okMsg) => {
        try {
            await update(ref(db, `mdm/devices/${mac}`), { command, feedback: 'pending' });
            toast.success(okMsg);
        } catch (e) {
            toast.error(`Failed: ${e.message}`);
        }
    };
    const scan = () => send('scan_all_apps', 'Scanning… list appears here in a few seconds');
    const clearList = async () => {
        try {
            await remove(ref(db, `mdm/devices/${mac}/all_apps`));
            toast.success('Scanned list cleared from the database');
        } catch (e) {
            toast.error(`Failed: ${e.message}`);
        }
    };
    const uninstall = (pkg) => () => {
        if (PROTECTED_PACKAGES.includes(pkg)) {
            toast.error(`Protected package — refused: ${pkg}`);
            return;
        }
        send(`uninstall:${pkg}`, `Uninstall ${pkg} sent`);
    };

    return (
        <section className="card-chrome overflow-hidden">
            <header className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
                    <FiSearch /> Scan all apps (full inventory)
                    {all.length > 0 && (
                        <span className="text-xs text-gray-500 font-normal">({all.length})</span>
                    )}
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                    {all.length > 0 && (
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Filter…"
                            className="bg-surface-inset border border-white/[0.06] rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand/40 min-w-[160px]"
                        />
                    )}
                    <button
                        onClick={scan}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/[0.05] hover:bg-white/[0.08] text-gray-100"
                    >
                        <FiRefreshCw /> Scan
                    </button>
                    {all.length > 0 && (
                        <button
                            onClick={clearList}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-400"
                        >
                            <FiTrash2 /> Clear list
                        </button>
                    )}
                </div>
            </header>

            <p className="px-5 py-2 text-xs text-gray-500 border-b border-white/[0.06]">
                One-time full inventory of every app on the box (not just tracked). Uninstall bloatware,
                then Clear. Protected apps (monitoring / updater / teamviewer) cannot be removed.
            </p>

            {!loading && all.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-gray-500">
                    No scan yet. Click "Scan" — results appear here in a few seconds.
                </div>
            ) : (
                <div className="max-h-[28rem] overflow-y-auto divide-y divide-white/[0.06]">
                    {shown.map((app, i) => {
                        const pkg = app.packageName;
                        const isProtected = PROTECTED_PACKAGES.includes(pkg);
                        return (
                            <div key={pkg || i} className="px-5 py-2 grid grid-cols-[1fr_auto] items-center gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm text-gray-100 truncate">{app.appName || pkg}</div>
                                    <div className="text-xs text-gray-500 font-mono truncate">
                                        {pkg}
                                        {app.appVersion ? ` · v${app.appVersion}` : ''}
                                    </div>
                                </div>
                                {isProtected ? (
                                    <span className="text-xs text-gray-600">protected</span>
                                ) : (
                                    <button
                                        onClick={uninstall(pkg)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/15 hover:bg-red-500/25 text-red-400"
                                    >
                                        <FiTrash2 /> Uninstall
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function SortableTh({ label, sortKey, sort, setSort }) {
    const isActive = sort.key === sortKey;
    const arrow = !isActive ? null : sort.dir === 'asc' ? <FiArrowUp /> : <FiArrowDown />;
    return (
        <th
            onClick={() =>
                setSort((s) =>
                    s.key === sortKey
                        ? { key: sortKey, dir: s.dir === 'asc' ? 'desc' : 'asc' }
                        : { key: sortKey, dir: 'asc' }
                )
            }
            className="px-5 py-2.5 text-left cursor-pointer select-none hover:text-brand-400"
        >
            <span className="inline-flex items-center gap-1">{label} {arrow}</span>
        </th>
    );
}

function AppRow({ row }) {
    return (
        <tr className="border-t border-white/[0.06] hover:bg-white/[0.02]">
            <td className="px-5 py-2.5">
                <div className="font-medium text-gray-100">{row.appName}</div>
                {row.tracked && (
                    <div className="text-[11px] text-brand-400 uppercase tracking-wider">tracked</div>
                )}
            </td>
            <td className="px-5 py-2.5 text-xs text-gray-400 font-mono break-all">
                {row.packageName || '—'}
            </td>
            <td className="px-5 py-2.5 text-gray-200">
                {row.installed || <span className="text-gray-500">—</span>}
                {row.tracked && row.latest && row.outdated && (
                    <span className="text-xs text-gray-500 ml-2">→ latest {row.latest}</span>
                )}
            </td>
            <td className="px-5 py-2.5">
                {!row.tracked ? (
                    <span className="text-xs text-gray-500">—</span>
                ) : row.outdated ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/15 text-orange-400">
                        Update available
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-400">
                        <FiCheckCircle /> Up to date
                    </span>
                )}
            </td>
        </tr>
    );
}
